import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, Save, X } from 'lucide-react';
import AuthContext from '../../../../context/AuthContext';
import { useToast } from '../../../../context/ToastContext';
import userService from '../../../../services/userService';

export default function EditUser() {
  const { id: userId } = useParams();
  const navigate = useNavigate();
  const { user, authTokens } = useContext(AuthContext);
  const { showSuccess, showError } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    contact: '',
    role_function: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const result = await userService.getUserDetails(userId, authTokens.access);
      
      if (result.success) {
        setUserData({
          first_name: result.data.first_name || '',
          last_name: result.data.last_name || '',
          email: result.data.email || '',
          contact: result.data.contact || '',
          role_function: result.data.role_function || '',
        });
      } else {
        showError(`Erreur: ${result.error}`);
        setTimeout(() => navigate('/dashboard/admin/users'), 2000);
      }
    } catch (error) {
      showError("Impossible de charger les détails de l'utilisateur");
      setTimeout(() => navigate('/dashboard/admin/users'), 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!userData.first_name.trim()) {
      newErrors.first_name = 'Le prénom est requis';
    }

    if (!userData.last_name.trim()) {
      newErrors.last_name = 'Le nom est requis';
    }

    // Email validation removed since it's read-only

    if (!userData.contact.trim()) {
      newErrors.contact = 'Le contact est requis';
    }
    if (!userData.role_function.trim()) {
      newErrors.role_function = 'Le Role est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showError('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    try {
      setSaving(true);
      setErrors({});

      const result = await userService.updateUser(userId, userData, authTokens.access);

      if (result.success) {
        showSuccess('Utilisateur mis à jour avec succès!');
        setTimeout(() => navigate('/dashboard/admin/users'), 1500);
      } else {
        if (result.status === 400 && result.errorData) {
          // Handle field-specific errors
          setErrors(result.errorData);
          showError('Veuillez corriger les erreurs dans le formulaire');
        } else if (result.status === 403) {
          showError('Vous n\'avez pas les permissions pour modifier cet utilisateur');
        } else {
          showError(`Erreur: ${result.error}`);
        }
      }
    } catch (error) {
      showError('Erreur réseau. Veuillez réessayer.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard/admin/users');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-6">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate('/dashboard/admin/users')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Retour aux utilisateurs
        </button>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-6">
            <User className="h-6 w-6 text-teal-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">Modifier l'utilisateur</h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First Name */}
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Prénom *
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={userData.first_name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    errors.first_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Entrez le prénom"
                />
                {errors.first_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Nom *
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={userData.last_name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    errors.last_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Entrez le nom"
                />
                {errors.last_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="h-4 w-4 inline mr-1" />
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={userData.email}
                readOnly
                className="w-full px-3 py-2 border rounded-md shadow-sm bg-gray-50 text-gray-600 cursor-not-allowed border-gray-300"
                placeholder="Entrez l'email"
              />
              <p className="mt-1 text-xs text-gray-500">
                L'email ne peut pas être modifié pour des raisons de sécurité
              </p>
            </div>

            {/* Contact */}
            <div>
              <label htmlFor="contact" className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="h-4 w-4 inline mr-1" />
                Contact *
              </label>
              <input
                type="text"
                id="contact"
                name="contact"
                value={userData.contact}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                  errors.contact ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Entrez le numéro de contact"
              />
              {errors.contact && (
                <p className="mt-1 text-sm text-red-600">{errors.contact}</p>
              )}
            </div>
            <div>
              <label htmlFor="contact" className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="h-4 w-4 inline mr-1" />
                Role/Position *
              </label>
              <input
                type="text"
                id="role_function"
                name="role_function"
                value={userData.role_function}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                  errors.role_function ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Entrez le role"
              />
              {errors.role_function && (
                <p className="mt-1 text-sm text-red-600">{errors.role_function}</p>
              )}
            </div>

            {/* General Error Display */}
            {errors.detail && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-600">{errors.detail}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCancel}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
              >
                <X className="h-4 w-4 mr-2" />
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Enregistrer
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
